import React, { useMemo, useRef, useState } from 'react'
import { Button, Card, Divider, Skeleton, Typography, message, Modal, Input, Image } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { useCartStore } from '../state/cartStore'
import { apiUrl } from '../config/api'
import Decimal from 'decimal.js-light'
import { formatAmount } from '../utils/amount'
import OtpAuthFlow from '../components/OtpAuthFlow.jsx'


function useDeliveryFee({ shopIds, orderTotal, deliveryLocationId }) {
  const enabled = shopIds.length > 0 && !!deliveryLocationId
  const roundedTotal = Math.round((Number(orderTotal) || 0) * 100) / 100
  const { data, isLoading } = useQuery({
    queryKey: ['deliveryFeeCalc', deliveryLocationId, shopIds.slice().sort().join(','), roundedTotal],
    enabled,
    queryFn: async () => {
      try {
        const url = apiUrl('/api/delivery-fee/calculate')
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            deliveryLocationId,
            shopsIds: shopIds,
            orderTotal: roundedTotal,
          })
        })
        if (!res.ok) throw new Error('Failed to calculate delivery fee')
        const json = await res.json()
        // Support possible response shapes: number or object with fee/deliveryFee
        const fee = (typeof json === 'number') ? json : (json?.fee ?? json?.deliveryFee ?? json?.data?.fee)
        const val = Number(fee)
        return isFinite(val) ? val : 0
      } catch {
        return 0
      }
    }
  })
  return { fee: data || 0, isLoading }
}

export default function Cart() {
  const [submitting, setSubmitting] = useState(false)
  const [notes, setNotes] = useState('')
  const { outSideWorkingHours, shift1Start, shift1End, shift2Start, shift2End, formatTimeArabic } = useOutletContext() || {}
  const navigate = useNavigate()

  // Address modal state
  const [addressModalOpen, setAddressModalOpen] = useState(false)
  const [address, setAddress] = useState('')
  const [loadingAddress, setLoadingAddress] = useState(false)

  // OTP auth (reusable component)
  const otpRef = useRef(null)
  const itemsMap = useCartStore(s => s.items)
  const setQty = useCartStore(s => s.setQty)
  const clear = useCartStore(s => s.clear)

  const items = Object.values(itemsMap)
  const byShop = useMemo(() => {
    const map = {}
    items.forEach(it => {
      if (!map[it.shopId]) map[it.shopId] = { shopId: it.shopId, shopName: it.shopName, displayGroupName: it.displayGroupName || it.shopName, items: [] }
      map[it.shopId].items.push(it)
    })
    return map
  }, [items])

  const shopIds = Object.keys(byShop).map(Number)

  const totals = useMemo(() => {
    const totalAfterD = items.reduce((s, it) => {
      const qty = Number(it.quantity) || 0
      const unit = new Decimal(it.price || 0)
      const bulkMin = Number(it.bulkMinQuantity)
      const hasBulk = Number.isFinite(bulkMin) && bulkMin > 0 && it.bulkSellPrice != null
      if (hasBulk && qty >= bulkMin) {
        const bulkPrice = new Decimal(it.bulkSellPrice || 0)
        const bundles = Math.floor(qty / bulkMin)
        const bulkQty = bundles * bulkMin
        const rem = qty - bulkQty
        return s.plus(bulkPrice.times(bulkQty)).plus(unit.times(rem))
      }
      return s.plus(unit.times(qty))
    }, new Decimal(0))
    const totalAfter = Number(totalAfterD.toFixed(2))
    return { totalAfter }
  }, [items])

  // Read delivery location id from localStorage
  const rawLoc = localStorage.getItem('deliveryLocation')
  let deliveryLocationId = null
  if (rawLoc) {
    try {
      const obj = JSON.parse(rawLoc)
      if (obj && typeof obj === 'object') deliveryLocationId = obj.id ?? null
    } catch {
      // ignore parse errors
    }
  }

  const { fee: deliveryFee, isLoading: loadingFee } = useDeliveryFee({
    shopIds,
    orderTotal: totals.totalAfter,
    deliveryLocationId,
  })

  const grandTotal = Number(new Decimal(totals.totalAfter || 0).plus(new Decimal(deliveryFee || 0)).toFixed(2))

  // Helpers
  function getDeliveryLocationId() {
    try {
      const raw = localStorage.getItem('deliveryLocation')
      if (!raw) return null
      const obj = JSON.parse(raw)
      return obj?.id || null
    } catch { return null }
  }

  function getUserInfo() {
    try {
      return {
        accessToken: localStorage.getItem('accessToken') || '',
        refreshToken: localStorage.getItem('refreshToken') || '',
      }
    } catch { return { accessToken: '', refreshToken: '' } }
  }

  function setUserInfo(data) {
    try {
      if (data.accessToken) localStorage.setItem('accessToken', data.accessToken)
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken)
    } catch { /* ignore */ }
  }

  function buildSubOrders() {
    const subOrders = []
    let missingCount = 0
    Object.values(byShop).forEach(shop => {
      const items = shop.items
        .filter(it => it.mapId && (it.quantity || 0) > 0)
        .map(it => ({ mapId: it.mapId, quantity: Number(it.quantity) || 0 }))
      const missing = shop.items.filter(it => !it.mapId).length
      missingCount += missing
      if (items.length > 0) {
        subOrders.push({ shopId: shop.shopId, pickupHour: null, items })
      }
    })
    return { subOrders, missingCount }
  }

  async function submitOrderNow() {
    const finalAddress = (address || '').trim()
    if (!finalAddress) {
      message.error('الرجاء إدخال عنوان التوصيل')
      return
    }

    try {
      setSubmitting(true)
      const deliveryLocationId = getDeliveryLocationId()
      if (!deliveryLocationId) {
        message.error('الرجاء اختيار موقع التوصيل أولاً')
        return
      }
      const { subOrders, missingCount } = buildSubOrders()
      if (subOrders.length === 0) {
        message.error('لا توجد عناصر صالحة لإرسال الطلب')
        return
      }
      if (missingCount > 0) {
        message.warning('تم تجاهل بعض العناصر القديمة لعدم توفر رقم التسعير الداخلي لها. يرجى إعادة إضافتها للسلة إذا لزم الأمر.')
      }

      // As per new API requirements, only submit subOrders
      const payload = {
        subOrders,
        notes: (notes || '').trim() || undefined,
        address: finalAddress,
      }
      const { accessToken } = getUserInfo()
      const { createOrder } = await import('../api/orders')
      const created = await createOrder(payload, accessToken)
      message.success('تم إنشاء الطلب بنجاح!')
      clear()
      setAddressModalOpen(false)
      navigate('/my-orders', { state: { fromOrderSuccess: true } })
    } catch (e) {
      console.error(e)
      message.error('تعذر إرسال الطلب، حاول مرة أخرى.')
    } finally {
      setSubmitting(false)
    }
  }

  function validatePhone(p) {
    return /^05\d{8}$/.test(String(p || '').trim())
  }

  async function fetchUserAddress() {
    try {
      setLoadingAddress(true)
      const { accessToken } = getUserInfo()
      if (!accessToken) return ''
      const res = await fetch(apiUrl('/api/users/me'), {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/json'
        }
      })
      if (res.ok) {
        const data = await res.json()
        return data.fullAddress || ''
      }
    } catch {
      // ignore
    } finally {
      setLoadingAddress(false)
    }
    return ''
  }

  async function startOrderFlow() {
    const { accessToken } = getUserInfo()
    if (accessToken) {
      // 1. Fetch current address
      const addr = await fetchUserAddress()
      setAddress(addr)
      // 2. Open confirmation modal
      setAddressModalOpen(true)
      return
    }
    otpRef.current?.open()
  }


  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Typography.Title level={3} style={{ margin: 0 }}>
        سلة المشتريات <Button style={{ marginRight: 5 }} onClick={() => navigate(-1)}>عودة</Button>
      </Typography.Title>
      {items.length === 0 ? (
        <Card>
          <Typography.Paragraph style={{ marginBottom: 12 }}>السلة فارغة.</Typography.Paragraph>
          <Button type="primary" onClick={() => navigate(-1)}>تصفح المنتجات</Button>
        </Card>
      ) : (
        <>
          <Card>
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>السعر الإجمالي</span>
                <strong>{formatAmount(totals.totalAfter)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>رسوم التوصيل</span>
                <strong>{loadingFee ? <Skeleton.Button active size="small" /> : formatAmount(deliveryFee)}</strong>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 22 }}>
                <span><strong>الإجمالي الكلي</strong></span>
                <strong>{formatAmount(grandTotal)}</strong>
              </div>
            </div>
          </Card>

          <Card>
            <div style={{ display: 'grid', gap: 8 }}>
              <label htmlFor="order-notes" style={{ fontWeight: 600 }}>ملاحظات الطلب (اختياري)</label>
              <Input.TextArea
                id="order-notes"
                style={{ minHeight: 80, resize: 'vertical' }}
                placeholder="مثال: الساندويتش بدون مخلل"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </Card>
          {outSideWorkingHours && (
            <Card size="small" style={{ background: '#fffbe6', border: '1px solid #ffe58f' }}>
              <Typography.Text type="warning" strong style={{ display: 'block', textAlign: 'center', marginBottom: 4 }}>
                نعتذر، نحن خارج ساعات العمل حالياً.
              </Typography.Text>
              <div style={{ textAlign: 'center', fontSize: 13 }}>
                <div>الفترة الأولى: {formatTimeArabic?.(shift1Start)} - {formatTimeArabic?.(shift1End)}</div>
                {shift2Start && shift2End && (
                  <div style={{ marginTop: 2 }}>الفترة الثانية: {formatTimeArabic?.(shift2Start)} - {formatTimeArabic?.(shift2End)}</div>
                )}
              </div>
            </Card>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <Button danger onClick={() => clear()}>تفريغ السلة</Button>
            <Button type="primary" block onClick={startOrderFlow} disabled={outSideWorkingHours}>
              {outSideWorkingHours ? 'الطلب غير متاح حالياً' : 'أطلب الآن'}
            </Button>
          </div>
          {Object.values(byShop).map((shop) => {
            const subTotal = Number(shop.items.reduce((s, it) => {
              const qty = Number(it.quantity) || 0
              const unit = new Decimal(it.price || 0)
              const bulkMin = Number(it.bulkMinQuantity)
              const hasBulk = Number.isFinite(bulkMin) && bulkMin > 0 && it.bulkSellPrice != null
              if (hasBulk && qty >= bulkMin) {
                const bulkPrice = new Decimal(it.bulkSellPrice || 0)
                const bundles = Math.floor(qty / bulkMin)
                const bulkQty = bundles * bulkMin
                const rem = qty - bulkQty
                return s.plus(bulkPrice.times(bulkQty)).plus(unit.times(rem))
              }
              return s.plus(unit.times(qty))
            }, new Decimal(0)).toFixed(2))
            return (
              <Card key={shop.shopId} title={shop.displayGroupName}>
                <div style={{ display: 'grid', gap: 8 }}>
                  {shop.items.map((it) => {
                    const qty = Number(it.quantity) || 0
                    const unit = new Decimal(it.price || 0)
                    const bulkMin = Number(it.bulkMinQuantity)
                    const hasBulk = Number.isFinite(bulkMin) && bulkMin > 0 && it.bulkSellPrice != null
                    const totalAfter = hasBulk && qty >= bulkMin
                      ? (() => { const bulkPrice = new Decimal(it.bulkSellPrice || 0); const bundles = Math.floor(qty / bulkMin); const bulkQty = bundles * bulkMin; const rem = qty - bulkQty; return bulkPrice.times(bulkQty).plus(unit.times(rem)); })()
                      : unit.times(qty)
                    return (
                      <div key={it.key} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <div style={{ width: 48, height: 48, overflow: 'hidden', background: '#fafafa', borderRadius: 4 }}>
                          <Image
                            src={`https://images.leen-go.com/${it.imageUrl}`}
                            alt={it.itemName}
                            width={48}
                            height={48}
                            style={{ objectFit: 'cover' }}
                            fallback="/no-image.png"
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <Typography.Text>{it.itemName}</Typography.Text>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <Typography.Text strong>{formatAmount(Number(it.price.toFixed(2)))} × {it.quantity} = {formatAmount(Number(totalAfter.toFixed(2)))}</Typography.Text>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Button size="small" onClick={() => setQty(it.key, it.quantity - 1)}>-</Button>
                          <span>{it.quantity}</span>
                          <Button size="small" onClick={() => setQty(it.key, it.quantity + 1)}>+</Button>
                        </div>
                      </div>
                    )
                  })}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                    <span>مجموع {shop.displayGroupName}</span>
                    <strong>{formatAmount(subTotal)}</strong>
                  </div>
                </div>
              </Card>
            )
          })}


          <OtpAuthFlow ref={otpRef} onSuccess={startOrderFlow} />

          {/* Address Confirmation Modal */}
          <Modal
            title="تأكيد عنوان التوصيل"
            open={addressModalOpen}
            onCancel={() => !submitting && setAddressModalOpen(false)}
            footer={[
              <Button key="cancel" disabled={submitting} onClick={() => setAddressModalOpen(false)}>
                إلغاء
              </Button>,
              <Button key="submit" type="primary" loading={submitting} onClick={submitOrderNow}>
                تأكيد الطلب
              </Button>
            ]}
          >
            {loadingAddress ? (
              <Skeleton active paragraph={{ rows: 2 }} />
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                <Typography.Text>الرجاء التأكد من عنوان التوصيل قبل إتمام الطلب:</Typography.Text>
                <Input.TextArea
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  placeholder="اكتب تفاصيل العنوان هنا (اسم الشارع، رقم العمارة، معلم قريب...)"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
                {!address && <Typography.Text type="danger" style={{ fontSize: 12 }}>* العنوان مطلوب</Typography.Text>}
              </div>
            )}
          </Modal>
        </>
      )}
    </div>
  )
}
